import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Input, Modal, Pagination, Select, Spin } from 'antd';

const PAGE_SIZE = 36;

export default function IconLibraryPicker({
  open,
  onClose,
  onSelect,
  token,
  isIconLibrary = false,
  bucket = 'pladdypusv3',
  prefix = 'full/',
  thumbPrefix = 'thumb/',
  apiPath = '/ts/get-s3-files',
  max,
  title,
  description,
  globalAssetLibrary = false,
  initialRatio,
}) {
  const [loading, setLoading] = useState(false);
  const [icons, setIcons] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [ratio, setRatio] = useState(initialRatio || null);
  const [total, setTotal] = useState(0);
  const API = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (!open) return;

    (async () => {
      setLoading(true);
      try {
        if (globalAssetLibrary) {
          const params = new URLSearchParams({
            page: String(page),
            pageSize: String(PAGE_SIZE),
          });
          if (search.trim()) params.append('tag', search.trim());
          if (ratio) params.append('ratio', ratio);

          const res = await fetch(`${API}/ts/common-library?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          setIcons(data.items || []);
          setTotal(Number(data.total || 0));
        } else {
          const res = await fetch(`${API}${apiPath}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              bucket,
              prefix,
              ...(max ? { max } : {}),
            }),
          });

          const data = await res.json();
          setIcons(data.items || []);
          setTotal((data.items || []).length);
        }
      } catch (err) {
        console.error('Failed to load icons:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, API, token, bucket, prefix, apiPath, max, globalAssetLibrary, page, search, ratio]);

  const filtered = useMemo(() => {
    if (globalAssetLibrary) return icons;
    const q = search.toLowerCase().trim();
    return icons.filter((item) => (item.key || item.url || '').toLowerCase().includes(q));
  }, [icons, search, globalAssetLibrary]);

  const pageItems = globalAssetLibrary
    ? filtered
    : filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSelect = (item) => {
    let finalUrl = item.url;
    if (globalAssetLibrary) {
      finalUrl = item.s3Url;
    }

    if (!globalAssetLibrary && !isIconLibrary && thumbPrefix) {
      finalUrl = item.url.replace('/thumb/', '/full/');
    }

    onSelect(finalUrl);
    onClose();
  };

  return (
    <Modal
      title={title || (isIconLibrary ? 'Select Icon' : 'Select Image')}
      open={open}
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnHidden
    >
      <p className="text-sm text-gray-500 mb-2">
        {description || (
          isIconLibrary
            ? 'These are system icons (transparent PNG).'
            : 'Select an image from your library.'
        )}
      </p>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {globalAssetLibrary && (
            <Select
              placeholder="All ratios"
              allowClear
              value={ratio}
              onChange={(val) => {
                setRatio(val || null);
                setPage(1);
              }}
              options={[
                { label: 'All types', value: null },
                { label: 'Carousel', value: '16:9' },
                { label: 'Square', value: '1:1' },
                { label: 'Banner', value: '3:1' },
              ]}
              style={{ width: 120 }}
            />
          )}
          <Input.Search
            placeholder={globalAssetLibrary ? 'Search' : 'Search...'}
            allowClear
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ width: 300 }}
            className="invisible"
          />
        </div>
        <span className="text-gray-500 text-sm">
          Total: {globalAssetLibrary ? total : filtered.length}
        </span>
      </div>

      {loading ? (
        <div className="py-10 text-center">
          <Spin />
        </div>
      ) : filtered.length === 0 ? (
        <Empty description="No files found" />
      ) : (
        <>
          <div className="grid grid-cols-6 gap-4 max-h-[420px] overflow-auto">
            {pageItems.map((item) => {
              const key = item.key || item.url || item.s3Url;
              return (
                <button
                  key={key}
                  className="border border-gray-300 rounded-lg p-2 bg-white hover:border-blue-500 transition"
                  onClick={() => handleSelect(item)}
                >
                  <img
                    src={item.url || item.s3Url}
                    alt={item.key || item.s3Url}
                    className="w-full h-[80px] object-contain bg-gray-200 rounded p-2"
                  />
                </button>
              );
            })}
          </div>

          <div className="mt-4 text-center">
            <Pagination
              current={page}
              total={globalAssetLibrary ? total : filtered.length}
              pageSize={PAGE_SIZE}
              onChange={(nextPage) => setPage(nextPage)}
              size="small"
              showSizeChanger={false}
            />
          </div>
        </>
      )}
    </Modal>
  );
}
